/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Card, Col, Collapse, Row, Skeleton, Space, Spin, Typography } from 'antd';
import { ApartmentOutlined, ArrowLeftOutlined, CheckOutlined, LoadingOutlined } from '@ant-design/icons';
import ActivitiesTable from '../Tables/ActivitiesTable';
import ActivityTypeTag from './ActivityTypeTag';
import MainActivityButton from './MainActivityButton';
import ActivityFlowEditor from './ActivityFlowEditor';

// Misc imports
import { useFetchActivityQuery, useUpdateActivityMutation } from '../../store/activities/activitiesApi';
import { ACTIVITY_TYPES } from '../../constants/main';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';

// Constant declarations
const { Text } = Typography;
export const PANEL_KEYS = {
  LIST: 'list',
  EDIT: 'edit'
};

const ActivitiesView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tenantId, orgId } = useParams();
  const { t } = useTranslation();
  const searchParams = new URLSearchParams(location.search);
  const { isEditingPrimaryLanguage, currentBotLanguage, primaryBotLanguage } = useBotLanguage();

  const { data: bot, isFetching: isFetchingBot } = getCurrentQueryState('fetchBot', { orgId, tenantId }, false);
  const { data: activity, isFetching: isFetchingActivity } = useFetchActivityQuery({ orgId, tenantId, activityId: searchParams.get('activity'), currentBotLanguage }, { skip: !searchParams.get('activity') });
  const [updateActivity, { isLoading: isUpdatingActivity }] = useUpdateActivityMutation({ fixedCacheKey: 'flowGraph-update-activity' });
  const [activePanelKey, setActivePanelKey] = useState(searchParams.has('activity') ? PANEL_KEYS.EDIT : PANEL_KEYS.LIST);
  const [currentActivityName, setCurrentActivityName] = useState(activity?.name?.[currentBotLanguage] || activity?.name?.[primaryBotLanguage]); // Keep activity name in the state because we need to update it without refetching

  useEffect(() => {
    setActivePanelKey(searchParams.has('activity') ? PANEL_KEYS.EDIT : PANEL_KEYS.LIST);
  }, [searchParams]);

  useEffect(() => {
    setCurrentActivityName(activity?.name?.[currentBotLanguage] || activity?.name?.[primaryBotLanguage]);
  }, [activity, currentBotLanguage]);

  const onBackToListClick = () => {
    searchParams.delete('activity');

    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    }, { replace: true });

    setActivePanelKey(PANEL_KEYS.LIST);
  };

  // Handle the update of the activity display name
  const onActivityNameSubmit = async (updatedName) => {
    if (updatedName && updatedName !== currentActivityName) {
      const prevValue = updatedName;

      setCurrentActivityName(updatedName);

      const { data: isSaveSuccessful } = await updateActivity({ orgId, tenantId, activityId: activity.id, data: { name: { ...activity.name, [currentBotLanguage]: updatedName } }, doNotInvalidate: false });

      // If save was not successful, fall back to the previous activity name
      if (!isSaveSuccessful) {
        setCurrentActivityName(prevValue);
      }
    }
  };

  const renderActivityName = () => {
    if (isFetchingActivity) {
      return (
        <Skeleton.Input active className="d-block opacity-70 border-radius-base" />
      );
    }

    if (activity) {
      return (
        <Row gutter={16} align="middle">
          <Col flex="none">
            <ActivityTypeTag type={activity.type} className="my-1 mr-0" />
          </Col>

          <Col>
            <MainActivityButton
              chaptersource={activity}
              buttonType="text"
              botId={tenantId}
              className="border-0"
              disabled={activity.type === ACTIVITY_TYPES.USER_FEEDBACK || !isEditingPrimaryLanguage}
            />
          </Col>

          <Col flex="auto">
            <Typography.Title
              editable={{
                triggerType: currentActivityName ? 'text' : 'icon',
                enterIcon: <CheckOutlined />,
                onChange: onActivityNameSubmit,
                autoSize: { maxRows: 1, minRows: 1 }
              }}
              level={5}
              className="mb-0"
            >
              {currentActivityName}
            </Typography.Title>
          </Col>

          {
            isUpdatingActivity ? (
              <Col flex="none">
                <Spin indicator={<LoadingOutlined className="text-secondary" spin />} />
              </Col>
            ) : null
          }
        </Row>
      );
    }

    // Else
    return null;
  };

  return (
    <Collapse
      accordion
      expandIcon={() => null}
      ghost
      activeKey={activePanelKey}
      className="ant-collapse-p-0"
    >
      <Collapse.Panel key={PANEL_KEYS.LIST}>
        <ActivitiesTable />
      </Collapse.Panel>

      <Collapse.Panel key={PANEL_KEYS.EDIT}>
        <Space className="mb-3">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={onBackToListClick}
            type="primary"
          />

          <Typography.Text>
            {t('Back to Flows')}
          </Typography.Text>
        </Space>

        <Card bordered={false} className="mb-5" loading={isFetchingBot && !bot}>
          <Row gutter={24} wrap={false} align="middle">
            <Col flex="none">
              <ApartmentOutlined className="fs-xxl text-primary" />
            </Col>
            <Col flex="auto">
              {renderActivityName()}

              <Text>
                {t('Drag and drop new nodes and connect them to create flows. To remove an element, select it and press Backspace (on Windows & Linux) or Delete (on Mac).')}
              </Text>
            </Col>
          </Row>
        </Card>

        <Card bordered={false} className="mb-5" loading={isFetchingBot && !bot}>
          <ActivityFlowEditor activityId={searchParams.get('activity')} />
        </Card>
      </Collapse.Panel>
    </Collapse>
  );
};

export default ActivitiesView;
